import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { DateTime } from 'luxon';
import { NAV_BAR_HEIGHT_PX } from '../../components/navBar/constants';
import RangeCalendar from '../../components/RangeCalendar';
import { useBackendPlaces } from '../../hooks/useBackendPlaces';
import { usePlacePredictions, type PlacePrediction } from '../../hooks/usePlacePredictions';
import { GuestsPicker } from './StayGuestComponents';
import {
  DEFAULT_GUESTS,
  dateRangeLabel,
  guestSummary,
  staySearchQuery,
  type StaySearchState,
} from './staySearchState';

/** Below this width the bar becomes one "Where to?" button and a full-screen sheet (Airbnb). */
const PHONE_QUERY = '(max-width: 743px)';

type Panel = 'where' | 'checkIn' | 'checkOut' | 'who';

export function useMatchMedia(query: string): boolean {
  const get = () => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return undefined;
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener?.('change', update);
    return () => list.removeEventListener?.('change', update);
  }, [query]);
  return matches;
}

const shortDate = (day?: string) => (day ? DateTime.fromISO(day).toFormat('LLL d') : undefined);

/** "2 guests" only once someone changed the count; Airbnb shows "Add guests" until then. */
function whoLabel(guests: StaySearchState['guests']): string | undefined {
  const untouched =
    guests.adults === DEFAULT_GUESTS.adults && !guests.children && !guests.infants && !guests.pets;
  return untouched ? undefined : guestSummary(guests);
}

// ─── icons ─────────────────────────────────────────────────────────────────

export function SearchGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={4}
    >
      <path d="M13 24a11 11 0 1 0 0-22 11 11 0 0 0 0 22zm8-3 9 9" />
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    >
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function NearbyGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    >
      <path d="M21 3 3 10.5l7.5 3L14 21z" />
    </svg>
  );
}

// ─── shared pieces ─────────────────────────────────────────────────────────

const SuggestionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 0;
`;

const SuggestionRow = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  border-radius: 12px;
  cursor: pointer;
  color: var(--color-text-primary);
  background: ${({ $active }) => ($active ? 'var(--color-bg-secondary)' : 'transparent')};
  &:hover {
    background: var(--color-bg-secondary);
  }
`;

const SuggestionIcon = styled.span`
  flex: 0 0 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
`;

const SuggestionText = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 15px;
  line-height: 20px;
  small {
    font-size: 13px;
    color: var(--color-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PanelHeading = styled.div`
  padding: 8px 16px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

function WhereSuggestions({
  predictions,
  activeIndex,
  onPick,
  onNearby,
  showNearby,
}: {
  predictions: PlacePrediction[];
  activeIndex: number;
  onPick: (prediction: PlacePrediction) => void;
  onNearby?: () => void;
  showNearby: boolean;
}) {
  return (
    <SuggestionList role="listbox" aria-label="Destinations" data-testid="stay-where-suggestions">
      {showNearby && onNearby && (
        <>
          <PanelHeading>Suggested destinations</PanelHeading>
          <SuggestionRow
            role="option"
            aria-selected={false}
            $active={false}
            onMouseDown={event => {
              event.preventDefault();
              onNearby();
            }}
          >
            <SuggestionIcon>
              <NearbyGlyph />
            </SuggestionIcon>
            <SuggestionText>
              Nearby
              <small>Find what's around you</small>
            </SuggestionText>
          </SuggestionRow>
        </>
      )}
      {predictions.map((prediction, index) => (
        <SuggestionRow
          key={prediction.placeId}
          role="option"
          aria-selected={index === activeIndex}
          $active={index === activeIndex}
          onMouseDown={event => {
            // Keep the input focused; the pick happens before blur.
            event.preventDefault();
            onPick(prediction);
          }}
        >
          <SuggestionIcon>
            <PinGlyph />
          </SuggestionIcon>
          <SuggestionText>
            {prediction.mainText}
            {prediction.secondaryText && <small>{prediction.secondaryText}</small>}
          </SuggestionText>
        </SuggestionRow>
      ))}
    </SuggestionList>
  );
}

const TextButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px 10px;
  margin-left: -10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;
  color: var(--color-text-primary);
  cursor: pointer;
  border-radius: 8px;
  &:hover {
    background: var(--color-bg-secondary);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

function NightsCalendar({
  checkIn,
  checkOut,
  onChange,
}: {
  checkIn?: string;
  checkOut?: string;
  onChange: (checkIn?: string, checkOut?: string) => void;
}) {
  const today = DateTime.now().toISODate() as string;
  return (
    <RangeCalendar
      mode="nights"
      value={{ start: checkIn ?? null, end: checkOut ?? null }}
      onChange={value => onChange(value.start ?? undefined, value.end ?? undefined)}
      minKey={today}
      maxKey={DateTime.fromISO(today).plus({ months: 18 }).toISODate() as string}
      todayKey={today}
    />
  );
}

/**
 * The "Where" field state: what is typed, the predictions, and the picked
 * point. Typing clears the point, so a search never uses the coordinates of
 * an earlier place; Search with typed text and no pick takes the first
 * prediction, like Airbnb.
 */
function useWhere(state: StaySearchState, setState: (patch: Partial<StaySearchState>) => void) {
  const fallback = useBackendPlaces();
  const places = usePlacePredictions({ types: ['(regions)'], fallback });
  const [activeIndex, setActiveIndex] = useState(-1);

  const type = (text: string) => {
    setState({ place: text, latitude: undefined, longitude: undefined });
    setActiveIndex(-1);
    places.search(text);
  };

  const pick = async (prediction: PlacePrediction) => {
    setState({ place: prediction.description });
    places.clear();
    const resolved = await places.resolve(prediction);
    if (resolved) {
      setState({
        place: prediction.description,
        latitude: resolved.latitude,
        longitude: resolved.longitude,
      });
    }
    return resolved;
  };

  const nearby = (onDone: () => void) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      position => {
        setState({
          place: 'Nearby',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        places.clear();
        onDone();
      },
      () => undefined,
      { timeout: 8000, maximumAge: 300000 },
    );
  };

  /** Coordinates for the typed place before searching. */
  const ensurePoint = async (current: StaySearchState): Promise<StaySearchState> => {
    if (!current.place.trim()) {
      return { ...current, place: '', latitude: undefined, longitude: undefined };
    }
    if (current.latitude !== undefined && current.longitude !== undefined) return current;
    const first = places.predictions[0];
    if (!first) return current;
    const resolved = await places.resolve(first);
    places.clear();
    return resolved
      ? {
          ...current,
          place: first.description,
          latitude: resolved.latitude,
          longitude: resolved.longitude,
        }
      : current;
  };

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>, onPicked: () => void) => {
    const count = places.predictions.length;
    if (event.key === 'ArrowDown' && count) {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % count);
    } else if (event.key === 'ArrowUp' && count) {
      event.preventDefault();
      setActiveIndex(index => (index <= 0 ? count - 1 : index - 1));
    } else if (event.key === 'Enter' && count) {
      event.preventDefault();
      pick(places.predictions[Math.max(0, activeIndex)]).then(onPicked);
    }
  };

  return { places, activeIndex, type, pick, nearby, ensurePoint, keyDown };
}

// ─── desktop bar ───────────────────────────────────────────────────────────

const Shell = styled.div<{ $open: boolean; $size: 'lg' | 'md' }>`
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: ${({ $size }) => ($size === 'lg' ? '850px' : '760px')};
  height: ${({ $size }) => ($size === 'lg' ? '66px' : '60px')};
  border-radius: 9999px;
  border: 1px solid var(--color-border);
  background: ${({ $open }) => ($open ? '#ebebeb' : 'var(--color-bg-elevated, #fff)')};
  box-shadow:
    0 3px 12px rgba(0, 0, 0, 0.1),
    0 1px 2px rgba(0, 0, 0, 0.08);
  color: var(--color-text-primary);
  text-align: left;
`;

const Seg = styled.div<{ $active: boolean; $flex: number; $open: boolean }>`
  position: relative;
  flex: ${({ $flex }) => $flex} 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  border-radius: 9999px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background: ${({ $active }) => ($active ? 'var(--color-bg-elevated, #fff)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 6px 20px rgba(0, 0, 0, 0.18)' : 'none')};
  z-index: ${({ $active }) => ($active ? 1 : 0)};
  &:hover {
    ${({ $active, $open }) =>
      !$active &&
      css`
        background: ${$open ? '#dddddd' : '#ebebeb'};
      `}
  }
`;

const SegBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 24px 0 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

const SegLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.01em;
  cursor: pointer;
`;

const SegValue = styled.span<{ $placeholder: boolean }>`
  font-size: 14px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $placeholder }) =>
    $placeholder ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'};
  font-weight: ${({ $placeholder }) => ($placeholder ? 400 : 600)};
`;

/* The global input look (border, focus ring) does not belong inside the pill. */
const bareInput = css`
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  outline: none;
  padding: 0;
`;

const WhereInput = styled.input`
  width: 100%;
  ${bareInput}
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-overflow: ellipsis;
  &::placeholder {
    color: var(--color-text-secondary);
    font-weight: 400;
  }
`;

const Sep = styled.span<{ $hidden: boolean }>`
  flex: 0 0 1px;
  align-self: center;
  height: 32px;
  background: var(--color-border);
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const SearchButton = styled.button<{ $wide: boolean; $size: 'lg' | 'md' }>`
  flex: 0 0 auto;
  margin: auto 8px auto 0;
  height: ${({ $size }) => ($size === 'lg' ? '48px' : '44px')};
  min-width: ${({ $size }) => ($size === 'lg' ? '48px' : '44px')};
  padding: ${({ $wide }) => ($wide ? '0 18px 0 16px' : '0')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 9999px;
  background: var(--color-black);
  color: var(--color-on-primary, #fff);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background: var(--color-black-hover, var(--color-black));
  }
`;

const Popover = styled.div<{ $align: 'left' | 'center' | 'right'; $width: string }>`
  position: absolute;
  top: calc(100% + 12px);
  ${({ $align }) =>
    $align === 'left'
      ? 'left: 0;'
      : $align === 'right'
        ? 'right: 0;'
        : 'left: 50%; transform: translateX(-50%);'}
  width: ${({ $width }) => $width};
  max-width: calc(100vw - 32px);
  padding: 16px;
  border-radius: 32px;
  background: var(--color-bg-elevated, #fff);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  z-index: 40;
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
`;

function DesktopBar({
  state,
  setState,
  onSearch,
  size,
  initialPanel,
  onClose,
}: {
  state: StaySearchState;
  setState: (patch: Partial<StaySearchState>) => void;
  onSearch: (state: StaySearchState) => void;
  size: 'lg' | 'md';
  initialPanel?: Panel;
  onClose?: () => void;
}) {
  const [panel, setPanel] = useState<Panel | null>(initialPanel ?? null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const where = useWhere(state, setState);

  useEffect(() => {
    if (panel === 'where') inputRef.current?.focus();
  }, [panel]);

  useEffect(() => {
    if (!panel) return undefined;
    const outside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setPanel(null);
        onClose?.();
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanel(null);
        onClose?.();
      }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [panel, onClose]);

  const submit = async () => {
    const ready = await where.ensurePoint(state);
    setPanel(null);
    onSearch(ready);
  };

  const nights =
    state.checkIn && state.checkOut
      ? Math.round(
          DateTime.fromISO(state.checkOut).diff(DateTime.fromISO(state.checkIn), 'days').days,
        )
      : 0;
  const open = panel !== null;
  const whereSuggestions = where.places.predictions.length > 0;
  const showNearby = !state.place && typeof navigator !== 'undefined' && !!navigator.geolocation;

  return (
    <Shell ref={rootRef} $open={open} $size={size} data-testid="stay-search-bar">
      <Seg $flex={1.4} $active={panel === 'where'} $open={open} onClick={() => setPanel('where')}>
        <SegBody>
          <SegLabel htmlFor="stay-where-input">Where</SegLabel>
          <WhereInput
            id="stay-where-input"
            ref={inputRef}
            value={state.place}
            placeholder="Search destinations"
            autoComplete="off"
            spellCheck={false}
            onFocus={() => setPanel('where')}
            onChange={event => where.type(event.target.value)}
            onKeyDown={event => where.keyDown(event, () => setPanel('checkIn'))}
            data-testid="stay-where-input"
          />
        </SegBody>
      </Seg>
      <Sep $hidden={panel === 'where' || panel === 'checkIn'} />
      <Seg
        $flex={1}
        $active={panel === 'checkIn'}
        $open={open}
        onClick={() => setPanel('checkIn')}
        data-testid="stay-search-dates"
      >
        <SegBody>
          <SegLabel as="span">Check in</SegLabel>
          <SegValue $placeholder={!state.checkIn}>
            {shortDate(state.checkIn) ?? 'Add dates'}
          </SegValue>
        </SegBody>
      </Seg>
      <Sep $hidden={panel === 'checkIn' || panel === 'checkOut'} />
      <Seg
        $flex={1}
        $active={panel === 'checkOut'}
        $open={open}
        onClick={() => setPanel('checkOut')}
      >
        <SegBody>
          <SegLabel as="span">Check out</SegLabel>
          <SegValue $placeholder={!state.checkOut}>
            {shortDate(state.checkOut) ?? 'Add dates'}
          </SegValue>
        </SegBody>
      </Seg>
      <Sep $hidden={panel === 'checkOut' || panel === 'who'} />
      <Seg
        $flex={open ? 1.7 : 1.3}
        $active={panel === 'who'}
        $open={open}
        onClick={() => setPanel('who')}
        data-testid="stay-search-guests"
      >
        <SegBody style={{ paddingRight: 8 }}>
          <SegLabel as="span">Who</SegLabel>
          <SegValue $placeholder={!whoLabel(state.guests)}>
            {whoLabel(state.guests) ?? 'Add guests'}
          </SegValue>
        </SegBody>
        <SearchButton
          type="button"
          $wide={open}
          $size={size}
          aria-label="Search"
          data-testid="stay-search-submit"
          onClick={event => {
            event.stopPropagation();
            submit();
          }}
        >
          <SearchGlyph />
          {open && <span>Search</span>}
        </SearchButton>
      </Seg>

      {panel === 'where' && (whereSuggestions || showNearby) && (
        <Popover $align="left" $width="425px">
          <WhereSuggestions
            predictions={where.places.predictions}
            activeIndex={where.activeIndex}
            showNearby={showNearby && !whereSuggestions}
            onNearby={() => where.nearby(() => setPanel('checkIn'))}
            onPick={prediction => {
              where.pick(prediction);
              setPanel('checkIn');
            }}
          />
        </Popover>
      )}
      {(panel === 'checkIn' || panel === 'checkOut') && (
        <Popover $align="center" $width="420px" data-testid="stay-dates-panel">
          <NightsCalendar
            checkIn={state.checkIn}
            checkOut={state.checkOut}
            onChange={(checkIn, checkOut) => {
              setState({ checkIn, checkOut });
              if (checkIn && checkOut) setPanel('who');
              else if (checkIn) setPanel('checkOut');
            }}
          />
          <PanelFooter>
            <span>{nights ? `${nights} night${nights === 1 ? '' : 's'}` : 'Pick your dates'}</span>
            <TextButton
              type="button"
              disabled={!state.checkIn}
              onClick={() => setState({ checkIn: undefined, checkOut: undefined })}
            >
              Clear dates
            </TextButton>
          </PanelFooter>
        </Popover>
      )}
      {panel === 'who' && (
        <Popover $align="right" $width="400px" style={{ padding: '16px 32px' }}>
          <GuestsPicker value={state.guests} onChange={guests => setState({ guests })} />
        </Popover>
      )}
    </Shell>
  );
}

// ─── phone: compact button + full-screen sheet ─────────────────────────────

const PhonePill = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 56px;
  padding: 0 20px;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
`;

const PhonePillText = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  small {
    font-size: 12px;
    font-weight: 400;
    color: var(--color-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: #f7f7f7;
`;

const SheetTop = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
`;

const CloseCircle = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: #fff;
  color: var(--color-text-primary);
  font-size: 18px;
  cursor: pointer;
`;

const SheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SheetCard = styled.div<{ $open: boolean }>`
  border-radius: 24px;
  background: #fff;
  box-shadow: ${({ $open }) =>
    $open ? '0 4px 24px rgba(0, 0, 0, 0.14)' : '0 1px 4px rgba(0, 0, 0, 0.08)'};
  padding: ${({ $open }) => ($open ? '24px 20px' : '18px 20px')};
`;

const CollapsedRow = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  cursor: pointer;
  strong {
    color: var(--color-text-primary);
    font-weight: 600;
  }
`;

const SheetTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 700;
  font-family: var(--font-display);
  color: var(--color-text-primary);
`;

const SheetInputBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  border: 1px solid var(--color-border-strong);
  border-radius: 12px;
  input {
    flex: 1;
    min-width: 0;
    ${bareInput}
    font-size: 16px;
    color: var(--color-text-primary);
  }
`;

const SheetFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--color-border);
  background: #fff;
`;

function PhoneSheet({
  state,
  setState,
  onSearch,
  onClose,
  initialPanel,
}: {
  state: StaySearchState;
  setState: (patch: Partial<StaySearchState>) => void;
  onSearch: (state: StaySearchState) => void;
  onClose: () => void;
  initialPanel: Panel;
}) {
  const [panel, setPanel] = useState<'where' | 'when' | 'who'>(
    initialPanel === 'who' ? 'who' : initialPanel === 'where' ? 'where' : 'when',
  );
  const where = useWhere(state, setState);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const submit = async () => {
    const ready = await where.ensurePoint(state);
    onSearch(ready);
    onClose();
  };
  const showNearby = !state.place && !!navigator.geolocation;

  return createPortal(
    <Sheet role="dialog" aria-modal="true" aria-label="Search" data-testid="stay-search-sheet">
      <SheetTop>
        <CloseCircle type="button" aria-label="Close" onClick={onClose}>
          ×
        </CloseCircle>
      </SheetTop>
      <SheetBody>
        <SheetCard $open={panel === 'where'}>
          {panel === 'where' ? (
            <>
              <SheetTitle>Where to?</SheetTitle>
              <SheetInputBox>
                <SearchGlyph />
                <input
                  autoFocus
                  value={state.place}
                  placeholder="Search destinations"
                  autoComplete="off"
                  onChange={event => where.type(event.target.value)}
                  onKeyDown={event => where.keyDown(event, () => setPanel('when'))}
                  data-testid="stay-where-input"
                />
              </SheetInputBox>
              <WhereSuggestions
                predictions={where.places.predictions}
                activeIndex={where.activeIndex}
                showNearby={showNearby && where.places.predictions.length === 0}
                onNearby={() => where.nearby(() => setPanel('when'))}
                onPick={prediction => {
                  where.pick(prediction);
                  setPanel('when');
                }}
              />
            </>
          ) : (
            <CollapsedRow type="button" onClick={() => setPanel('where')}>
              <span>Where</span>
              <strong>{state.place || "I'm flexible"}</strong>
            </CollapsedRow>
          )}
        </SheetCard>
        <SheetCard $open={panel === 'when'}>
          {panel === 'when' ? (
            <>
              <SheetTitle>When's your trip?</SheetTitle>
              <NightsCalendar
                checkIn={state.checkIn}
                checkOut={state.checkOut}
                onChange={(checkIn, checkOut) => {
                  setState({ checkIn, checkOut });
                  if (checkIn && checkOut) setPanel('who');
                }}
              />
            </>
          ) : (
            <CollapsedRow type="button" onClick={() => setPanel('when')}>
              <span>When</span>
              <strong>
                {state.checkIn && state.checkOut
                  ? dateRangeLabel(state.checkIn, state.checkOut)
                  : 'Add dates'}
              </strong>
            </CollapsedRow>
          )}
        </SheetCard>
        <SheetCard $open={panel === 'who'}>
          {panel === 'who' ? (
            <>
              <SheetTitle>Who's coming?</SheetTitle>
              <GuestsPicker value={state.guests} onChange={guests => setState({ guests })} />
            </>
          ) : (
            <CollapsedRow type="button" onClick={() => setPanel('who')}>
              <span>Who</span>
              <strong>{whoLabel(state.guests) ?? 'Add guests'}</strong>
            </CollapsedRow>
          )}
        </SheetCard>
      </SheetBody>
      <SheetFooter>
        <TextButton
          type="button"
          onClick={() =>
            setState({
              place: '',
              latitude: undefined,
              longitude: undefined,
              checkIn: undefined,
              checkOut: undefined,
              guests: DEFAULT_GUESTS,
            })
          }
        >
          Clear all
        </TextButton>
        <SearchButton
          type="button"
          $wide
          $size="lg"
          onClick={submit}
          data-testid="stay-search-submit"
        >
          <SearchGlyph />
          <span>Search</span>
        </SearchButton>
      </SheetFooter>
    </Sheet>,
    document.body,
  );
}

// ─── compact pill for the header (search page) ─────────────────────────────

const CompactPill = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  max-width: 560px;
  width: 100%;
  margin: 0 auto;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  }
`;

const CompactPart = styled.button<{ $muted?: boolean; $grow?: number }>`
  flex: ${({ $grow }) => $grow ?? 1} 1 0;
  min-width: 0;
  height: 100%;
  padding: 0 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
  color: ${({ $muted }) => ($muted ? 'var(--color-text-secondary)' : 'var(--color-text-primary)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const CompactSep = styled.span`
  flex: 0 0 1px;
  height: 24px;
  background: var(--color-border);
`;

const CompactIcon = styled.span`
  flex: 0 0 34px;
  height: 34px;
  margin-right: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: var(--color-black);
  color: var(--color-on-primary, #fff);
`;

const Overlay = styled.div`
  position: fixed;
  inset: ${NAV_BAR_HEIGHT_PX}px 0 0 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.25);
`;

const OverlayBand = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: ${NAV_BAR_HEIGHT_PX - 1}px;
  z-index: 61;
  display: flex;
  justify-content: center;
  padding: 8px 24px 24px;
  background: var(--color-bg-elevated, #fff);
  border-bottom: 1px solid var(--color-border);
`;

// ─── public component ──────────────────────────────────────────────────────

/**
 * The stays search (spec 016 W11), shaped like Airbnb's:
 * - `size="lg"`: the home page's big Where | Check in | Check out | Who bar;
 * - `variant="compact"`: the header pill on the results page, which opens the full bar;
 * - phones get one "Where to?" button that opens a full-screen sheet.
 * Without `onSearch` it goes to `/stays/search`.
 */
export function StaySearchBar({
  initial,
  onSearch,
  variant = 'full',
  size = 'lg',
}: {
  initial: StaySearchState;
  onSearch?: (state: StaySearchState) => void;
  variant?: 'full' | 'compact';
  size?: 'lg' | 'md';
}) {
  const navigate = useNavigate();
  const phone = useMatchMedia(PHONE_QUERY);
  const [state, setStateRaw] = useState(initial);
  const [sheet, setSheet] = useState<Panel | null>(null);
  const [expanded, setExpanded] = useState<Panel | null>(null);
  useEffect(() => setStateRaw(initial), [initial]);
  const setState = useCallback(
    (patch: Partial<StaySearchState>) => setStateRaw(previous => ({ ...previous, ...patch })),
    [],
  );

  const run = (next: StaySearchState) => {
    // A new place or new dates clear the map box from an earlier "search this area".
    const ready = { ...next, bounds: undefined };
    setExpanded(null);
    if (onSearch) onSearch(ready);
    else navigate(`/stays/search?${staySearchQuery(ready)}`);
  };

  const summary = useMemo(
    () => ({
      where: initial.place || 'Anywhere',
      when:
        initial.checkIn && initial.checkOut
          ? dateRangeLabel(initial.checkIn, initial.checkOut)
          : 'Any week',
      who: whoLabel(initial.guests),
    }),
    [initial],
  );

  if (phone) {
    return (
      <>
        <PhonePill type="button" onClick={() => setSheet('where')} data-testid="stay-search-bar">
          <SearchGlyph size={18} />
          <PhonePillText>
            {variant === 'compact' && initial.place ? initial.place : 'Where to?'}
            <small>{[summary.when, summary.who ?? 'Add guests'].join(' · ')}</small>
          </PhonePillText>
        </PhonePill>
        {sheet && (
          <PhoneSheet
            state={state}
            setState={setState}
            onSearch={run}
            onClose={() => {
              setSheet(null);
              setStateRaw(initial);
            }}
            initialPanel={sheet}
          />
        )}
      </>
    );
  }

  if (variant === 'compact') {
    const close = () => {
      setExpanded(null);
      setStateRaw(initial);
    };
    return (
      <>
        <CompactPill data-testid="stay-search-compact">
          <CompactPart type="button" $grow={1.3} onClick={() => setExpanded('where')}>
            {summary.where}
          </CompactPart>
          <CompactSep />
          <CompactPart type="button" onClick={() => setExpanded('checkIn')}>
            {summary.when}
          </CompactPart>
          <CompactSep />
          <CompactPart type="button" $muted={!summary.who} onClick={() => setExpanded('who')}>
            {summary.who ?? 'Add guests'}
          </CompactPart>
          <CompactIcon aria-hidden="true">
            <SearchGlyph size={12} />
          </CompactIcon>
        </CompactPill>
        {expanded &&
          createPortal(
            <>
              <Overlay onClick={close} />
              <OverlayBand>
                <DesktopBar
                  state={state}
                  setState={setState}
                  onSearch={run}
                  size="md"
                  initialPanel={expanded}
                  onClose={close}
                />
              </OverlayBand>
            </>,
            document.body,
          )}
      </>
    );
  }

  return <DesktopBar state={state} setState={setState} onSearch={run} size={size} />;
}
